import { auth } from '../firebase/config';
import { 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider
} from 'firebase/auth';

export const authService = {
  // Google Sign In
  signInWithGoogle: async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      
      return {
        success: true,
        user: {
          uid: result.user.uid,
          name: result.user.displayName || result.user.email.split('@')[0],
          email: result.user.email,
          photoURL: result.user.photoURL,
          emailVerified: result.user.emailVerified
        }
      };
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  },

  // Sign up - create account but don't allow login until verified
  signUpWithEmail: async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Send verification email immediately
      await sendEmailVerification(result.user);
      
      // Sign out the user immediately after creating account
      await signOut(auth);
      
      return {
        success: true,
        user: {
          uid: result.user.uid,
          name: result.user.displayName || result.user.email.split('@')[0],
          email: result.user.email,
          photoURL: result.user.photoURL,
          emailVerified: false
        },
        needsVerification: true
      };
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  },

  // Sign in - BLOCK unverified users
  signInWithEmail: async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Force refresh the user to get latest verification status
      await result.user.reload();
      
      if (!result.user.emailVerified) {
        // Sign out the unverified user immediately
        await signOut(auth);
        
        return {
          success: false,
          error: 'Please verify your email address before signing in. Check your inbox for the verification link.',
          needsVerification: true,
          user: {
            uid: result.user.uid,
            email: result.user.email
          }
        };
      }
      
      // Only allow verified users to proceed
      return {
        success: true,
        user: {
          uid: result.user.uid,
          name: result.user.displayName || result.user.email.split('@')[0],
          email: result.user.email,
          photoURL: result.user.photoURL,
          emailVerified: result.user.emailVerified
        }
      };
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  },

  // Sign Out
  signOut: async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Auth State Observer
  onAuthStateChanged: (callback) => {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Reload user to get fresh verification status
        await user.reload();
        
        if (!user.emailVerified) {
          // If user is not verified, sign them out
          await signOut(auth);
          callback(null);
          return;
        }
        
        // Only call callback with verified users
        callback({
          uid: user.uid,
          name: user.displayName || user.email.split('@')[0],
          email: user.email,
          photoURL: user.photoURL,
          emailVerified: user.emailVerified
        });
      } else {
        callback(null);
      }
    });
  },

  // Resend verification email for existing users
  resendVerificationEmail: async (email, password) => {
    try {
      // Sign in temporarily to send verification
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      if (result.user.emailVerified) {
        await signOut(auth);
        return {
          success: false,
          error: 'Email is already verified. You can sign in normally.'
        };
      }
      
      // Send verification email
      await sendEmailVerification(result.user);
      
      // Sign out immediately
      await signOut(auth);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  },

  // Helper method for error messages
  getErrorMessage: (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'An account with this email already exists.';
      case 'auth/weak-password':
        return 'Password should be at least 6 characters.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-not-found':
        return 'No account found with this email.';
      case 'auth/wrong-password':
        return 'Incorrect password.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      default:
        return 'An error occurred. Please try again.';
    }
  },
};